import React from "react";
import { NavLink } from "react-router-dom";
import "../styles/HomepageS9.css";

export default function HomepageS9() {
  /*
   * TIME-SENSITIVE ALERT
   *
   * Set enabled to true when an initiative has an active announcement.
   * Add an ISO expiry date supplied by the content owner.
   *
   * After the date passes:
   * - The alert CTA disappears.
   * - The expired alert text is replaced by statusAfterExpiry.
   *
   * Example date:
   * "2026-09-30T23:59:59-07:00"
   */
  const initiativeAlert = {
    enabled: false,
    label: "Open Call",
    title: "Peer-review participation is now open",
    description:
      "Qualified professionals are invited to support the current review period for the Global OSH Metrics Initiative.",
    link: "/initiatives/global-osh-metrics",
    cta: "Review Participation Details",
    expiresAt: null,
    statusAfterExpiry:
      "The current peer-review recruitment period has closed. Initiative updates remain available on the project page.",
  };

  const initiatives = [
    {
      number: "01",
      category: "Global Research",
      title: "Global OSH Metrics Initiative",
      description:
        "Advancing credible, practical approaches to occupational health and safety measurement through international review and collaboration.",
      status: "Peer review and development activity",
      statusAfterExpiry: "Peer-review period completed",
      image: "/assets/image1.jpg",
      link: "/partnerships",
      cta: "Explore the Initiative",

      /*
       * Add a date only when this CTA is tied to a limited activity,
       * recruitment period, submission window, or event.
       */
      ctaExpiresAt: null,
    },
    {
      number: "02",
      category: "Digital Learning",
      title: "Global Digital & Immersive Learning Task Force",
      description:
        "Bringing together global expertise to explore effective digital, immersive, and technology-enabled safety learning.",
      status: "Consultation and pilot development",
      statusAfterExpiry: "Pilot-development period completed",
      image: "/assets/2vr.jpg",
      link: "/ai-driven-safety-solutions",
      cta: "Explore the Task Force",
      ctaExpiresAt: null,
    },
    {
      number: "03",
      category: "Leadership Network",
      title: "Canadian BioPharma EHS Leadership Forum",
      description:
        "A practitioner-led exploratory initiative coordinated by What Works Institute, with GESI serving as Strategic Advisor.",
      status: "Exploratory initiative",
      statusAfterExpiry: "Exploratory phase completed",
      image: "/assets/Partnership.jpg",
      link: "/partnerships",
      cta: "Explore the Forum",
      ctaExpiresAt: null,
    },
  ];

  const currentTime = Date.now();

  const hasExpired = (date) => {
    if (!date) {
      return false;
    }

    const expiryTime = new Date(date).getTime();

    if (Number.isNaN(expiryTime)) {
      console.warn(`Invalid initiative expiry date: ${date}`);
      return false;
    }

    return currentTime > expiryTime;
  };

  const alertExpired =
    initiativeAlert.enabled && hasExpired(initiativeAlert.expiresAt);

  return (
    // HOMEPAGE SECTION 9
    <section
      id="homepage-section-nine"
      className="homepage-s9"
      aria-labelledby="homepage-s9-title"
    >
      <div className="homepage-s9-background" aria-hidden="true">
        <span className="homepage-s9-glow homepage-s9-glow-left"></span>
        <span className="homepage-s9-glow homepage-s9-glow-right"></span>
      </div>

      <div className="homepage-s9-container">
        {/* TIME-SENSITIVE ALERT */}
        {initiativeAlert.enabled && (
          <aside
            className={`homepage-s9-alert ${
              alertExpired ? "homepage-s9-alert-expired" : ""
            }`}
            aria-label="Initiative update"
          >
            <div className="homepage-s9-alert-icon" aria-hidden="true">
              {alertExpired ? "✓" : "!"}
            </div>

            <div className="homepage-s9-alert-copy">
              <p className="homepage-s9-alert-label">
                {alertExpired ? "Status Update" : initiativeAlert.label}
              </p>

              <h3>{initiativeAlert.title}</h3>

              <p>
                {alertExpired
                  ? initiativeAlert.statusAfterExpiry
                  : initiativeAlert.description}
              </p>
            </div>

            {!alertExpired && initiativeAlert.link && (
              <NavLink
                to={initiativeAlert.link}
                className="homepage-s9-alert-link"
              >
                <span>{initiativeAlert.cta}</span>
                <span aria-hidden="true">↗</span>
              </NavLink>
            )}
          </aside>
        )}

        <div className="homepage-s9-heading">
          <p className="homepage-s9-kicker">
            Leadership in Action
          </p>

          <h2 id="homepage-s9-title" className="homepage-s9-title">
            Featured
            <span> Initiatives</span>
          </h2>

          <p className="homepage-s9-intro">
            Current initiatives that bring together leadership, research,
            technology, and global collaboration. Detailed updates and
            participation information are available on each initiative page.
          </p>
        </div>

        <div className="homepage-s9-grid">
          {initiatives.map((initiative) => {
            const ctaExpired = hasExpired(initiative.ctaExpiresAt);

            return (
              <article
                className="homepage-s9-card"
                key={initiative.title}
              >
                <div className="homepage-s9-image-wrapper">
                  <img
                    src={initiative.image}
                    alt=""
                    className="homepage-s9-image"
                    loading="lazy"
                  />

                  <div
                    className="homepage-s9-image-overlay"
                    aria-hidden="true"
                  ></div>

                  <span
                    className="homepage-s9-card-number"
                    aria-hidden="true"
                  >
                    {initiative.number}
                  </span>

                  <span className="homepage-s9-category">
                    {initiative.category}
                  </span>
                </div>

                <div className="homepage-s9-card-content">
                  <div>
                    <div
                      className={`homepage-s9-status ${
                        ctaExpired ? "homepage-s9-status-complete" : ""
                      }`}
                    >
                      <span
                        className="homepage-s9-status-dot"
                        aria-hidden="true"
                      ></span>

                      <span>
                        {ctaExpired
                          ? initiative.statusAfterExpiry
                          : initiative.status}
                      </span>
                    </div>

                    <h3>{initiative.title}</h3>

                    <p className="homepage-s9-card-description">
                      {initiative.description}
                    </p>
                  </div>

                  {!ctaExpired && (
                    <NavLink
                      to={initiative.link}
                      className="homepage-s9-card-link"
                      aria-label={`${initiative.cta}: ${initiative.title}`}
                    >
                      <span>{initiative.cta}</span>

                      <span
                        className="homepage-s9-card-arrow"
                        aria-hidden="true"
                      >
                        ↗
                      </span>
                    </NavLink>
                  )}

                  {ctaExpired && (
                    <NavLink
                      to={initiative.link}
                      className="homepage-s9-card-link homepage-s9-archive-link"
                      aria-label={`View updates for ${initiative.title}`}
                    >
                      <span>View Initiative Updates</span>

                      <span
                        className="homepage-s9-card-arrow"
                        aria-hidden="true"
                      >
                        →
                      </span>
                    </NavLink>
                  )}
                </div>

                <span
                  className="homepage-s9-card-line"
                  aria-hidden="true"
                ></span>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}